export interface PilotApplication {
  fullName: string;
  email: string;
  phone: string;
  company: string;
  role: string;
  teamSize: string;
  repositoryUrl?: string;
  challenges: string;
  hearAbout?: string;
  codebaseSize?: string;
}

interface SubmitResponse {
  success: boolean;
  message: string;
  warning?: string | null;
  applicationId?: string;
  details?: {
    databaseSaved: boolean;
    emailSent: boolean;
    emailError?: string | null;
  };
}

/**
 * 🎯 ROBUST APPLICATION SUBMISSION
 * 
 * Submits pilot application via Next.js API route:
 * - Primary: Saves to Supabase database (REQUIRED)
 * - Secondary: Sends email notification (OPTIONAL)
 * 
 * Returns success if database save succeeds, even if email fails.
 * Provides clear feedback about email status in warnings.
 */
export const submitPilotApplication = async (
  applicationData: PilotApplication
): Promise<SubmitResponse> => {
  try {
    console.log('🚀 Submitting pilot application...', {
      email: applicationData.email,
      company: applicationData.company,
    });

    // Call Next.js API route which handles database + email
    // NOTE: repository_url is intentionally excluded from API submission
    // to avoid Supabase PGRST204 schema cache errors. The field is kept
    // in the form for user reference but not saved to the database.
    const response = await fetch('/api/pilot-application', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        full_name: applicationData.fullName,
        email: applicationData.email,
        phone: applicationData.phone,
        company: applicationData.company,
        role: applicationData.role,
        team_size: applicationData.teamSize,
        // repository_url: REMOVED - not saved to database to avoid schema conflicts
        challenges: applicationData.challenges,
        hear_about: applicationData.hearAbout,
        codebase_size: applicationData.codebaseSize,
      }),
    });

    console.log('📡 API Response status:', response.status, response.statusText);

    // Try to parse JSON response
    let result: any;
    try {
      const responseText = await response.text();
      console.log('📄 Raw API response:', responseText.substring(0, 500));
      
      result = JSON.parse(responseText);
    } catch (parseError: any) {
      console.error('❌ Failed to parse API response:', parseError);
      throw new Error(
        'Unable to process server response. Please try again or contact support at markovic@veriqon.ai'
      );
    }

    if (!response.ok) {
      // API returned an error
      console.error('❌ API returned error:', {
        status: response.status,
        result,
      });
      
      throw new Error(
        result.message || 
        result.error || 
        `Server error (${response.status}). Please try again or contact support at markovic@veriqon.ai`
      );
    }

    // Success - application saved to database
    console.log('✅ Application submitted successfully:', result.applicationId);
    
    return {
      success: true,
      message: result.message || 'Application submitted successfully!',
      warning: result.warning || null,
      applicationId: result.applicationId,
      details: result.details,
    };
  } catch (error: any) {
    console.error('❌ Application submission error:', error);

    // Provide helpful error message
    const errorMessage = error.message || 
      'Unable to submit application. Please try again or contact support at markovic@veriqon.ai';

    throw new Error(errorMessage);
  }
};